import { useCallback, useEffect, useRef, useState } from "react";

/** Open delay before the first flyout appears (design handoff §11: ~80ms). */
export const FLYOUT_OPEN_DELAY_MS = 80;
/** Grace period before a flyout closes once the pointer leaves (~150ms). */
export const FLYOUT_CLOSE_GRACE_MS = 150;

/**
 * Hover intent for the nav rail's section flyouts: a short open delay so
 * sweeping past the rail doesn't flash a panel, instant switching between
 * sections once one is open, and a close grace period so the pointer can
 * cross from the rail to the flyout without it flickering closed.
 *
 * Clicking a section pins its flyout open: the pointer leaving no longer
 * closes it, so it waits until the user picks a page, presses outside or
 * hits Escape (`close`). Hovering another section while pinned switches to
 * it and stays pinned.
 */
export function useFlyoutHover<K extends string>() {
  const [openKey, setOpenKey] = useState<K | null>(null);
  const openKeyRef = useRef<K | null>(null);
  const pinned = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const set = useCallback((key: K | null) => {
    openKeyRef.current = key;
    setOpenKey(key);
  }, []);

  const cancel = useCallback(() => clearTimeout(timer.current), []);

  /** Hover / focus: open after the delay, or switch at once if one is open. */
  const hoverOpen = useCallback(
    (key: K) => {
      cancel();
      if (openKeyRef.current) set(key);
      else timer.current = setTimeout(() => set(key), FLYOUT_OPEN_DELAY_MS);
    },
    [cancel, set],
  );

  /** Click: open immediately and pin (never closes an open section). */
  const pin = useCallback(
    (key: K) => {
      cancel();
      pinned.current = true;
      set(key);
    },
    [cancel, set],
  );

  /** Pointer left: close after the grace period, unless pinned. */
  const closeSoon = useCallback(() => {
    cancel();
    if (pinned.current) return;
    timer.current = setTimeout(() => set(null), FLYOUT_CLOSE_GRACE_MS);
  }, [cancel, set]);

  const close = useCallback(() => {
    cancel();
    pinned.current = false;
    set(null);
  }, [cancel, set]);

  useEffect(() => cancel, [cancel]);

  return { openKey, hoverOpen, pin, closeSoon, cancel, close };
}
